import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subject, Subscription, map, switchMap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { UserService } from '../../../../services/user.service';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { StudentPostsService } from '../../../../services/student-posts.service';
import { InterestService } from '../../../../services/interests.service';
import { CareerPageService } from '../../../../services/career-page.service';
import { Post } from '../../../../models/post';
import { User } from '../../../../models/user';
import { Interest } from '../../../../models/interest';
import { Comment } from '../../../../models/comment.js';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ReportDialog } from '../../../common/report-dialog/report-dialog';
import { HideConfirmDialog } from '../../../common/hide-confirm-dialog/hide-confirm-dialog';
import { UserLink } from '../../../common/user-link/user-link';
import { UserAvatar } from '../../../common/user-avatar/user-avatar';
import { LinkedEvents } from '../../../common/linked-events/linked-events';
import { StudentPostDialog } from '../../../common/student-post-dialog/student-post-dialog';
import { USER_PERMISSIONS } from '../../../../constants/permissions';
import { timeAgo } from '../../../../helpers/time-ago';

/** Which set of posts the feed is showing: the normal feed, or only the student's own posts. */
export type FeedMode = 'all' | 'mine';

/** Minimal shape needed from a Career record for the filter chips. */
interface CareerOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-feed-column',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatDividerModule,
    MatInputModule,
    MatFormFieldModule,
    MatMenuModule,
    FormsModule,
    UserLink,
    UserAvatar,
    LinkedEvents,
  ],
  templateUrl: './feed-column.html',
  styleUrl: './feed-column.css',
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-16px)' }),
        animate('550ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('slideDown', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('180ms ease-in', style({ height: '0', opacity: 0 })),
      ]),
    ]),
  ],
})
export class FeedColumn implements OnInit, OnDestroy {
  private sub!: Subscription;
  private querySub!: Subscription;
  private postChangedSub!: Subscription;
  private loadSub!: Subscription;
  private initialized = false;

  /**
   * Drives every post fetch — pagination (loadPosts) and the live-update
   * refresh (refreshPostsLive) alike — so a newer request always cancels
   * one still in flight, regardless of which of the two triggered it.
   * Without this, loadPosts(true)'s reset-then-append and
   * refreshPostsLive()'s replace could both be in flight at once: if the
   * replace resolved first, the append then landed on top of it instead of
   * the empty array it expected, duplicating every post in view.
   */
  private loadTrigger$ = new Subject<'paginated' | 'live'>();

  studentPostService = inject(StudentPostsService);
  userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private interestService = inject(InterestService);
  private careerPageService = inject(CareerPageService);

  /** Used to manually trigger change detection after async updates. */
  cdr = inject(ChangeDetectorRef);

  /** Currently loaded post entries displayed in the feed. */
  posts = signal<Post[]>([]);

  /** Tracks which post's comment section is currently expanded. Only one open at a time. */
  openCommentsPostId: number | null = null;

  /** Loaded comments keyed by post ID. Populated on demand when a post is expanded. */
  comments: { [postId: number]: Comment[] } = {};

  /** Tracks the draft comment text for each post's input field. */
  newComment: { [postId: number]: string } = {};

  /** Tracks the draft reply text for each comment's reply input. */
  replyText: { [commentId: number]: string } = {};

  /** The comment ID currently showing a reply input, or null if none. */
  activeReplyId: number | null = null;

  /** Page for pagination. */
  page = signal(0);
  /** Limit of posts grabbed at one time. */
  limit = 10;
  /** Are we loading more posts in. */
  isLoading = signal(false);
  /** Are there any posts left to load. */
  hasMore = signal(true);
  searchQuery = '';

  /** All available careers, for the feed's career filter chips. */
  careers = signal<CareerOption[]>([]);
  /** All available interests, for the feed's interest filter chips. */
  interests = signal<Interest[]>([]);
  /** Career IDs currently selected in the filter chips. */
  selectedCareerIds = signal<Set<number>>(new Set());
  /** Interest IDs currently selected in the filter chips. */
  selectedInterestIds = signal<Set<number>>(new Set());
  /**
   * Array forms of the two selection sets, for binding to mat-chip-listbox's
   * [value] — the listbox needs an array it can compare against each
   * option's own [value], not a Set.
   */
  selectedCareerIdsArray = computed(() => Array.from(this.selectedCareerIds()));
  selectedInterestIdsArray = computed(() => Array.from(this.selectedInterestIds()));

  /** Whether the career/interest filter panel is currently expanded. Collapsed by default. */
  filtersOpen = signal(false);

  /** Relative "Xd ago" label for a post's creation time. */
  timeAgo = timeAgo;

  /**
   * Whether a post date can be rendered (missing `createdAt` yields Invalid Date).
   * @param {Date | null | undefined} date
   * @returns {boolean}
   */
  isValidDate(date: Date | null | undefined): date is Date {
    return date instanceof Date && !Number.isNaN(date.getTime());
  }

  /** Whether the feed shows every visible post or only the current student's own. */
  feedMode = signal<FeedMode>('all');

  /** Whether the current user may author posts (drives the Create Post button). */
  get canCreatePost(): boolean {
    return !!this.userService.currentUser?.hasPermission(USER_PERMISSIONS.POSTS.CREATE);
  }

  /** Whether the current user may list their own posts (drives the My Posts toggle). */
  get canViewOwnPosts(): boolean {
    return !!this.userService.currentUser?.hasPermission(USER_PERMISSIONS.POSTS.VIEW);
  }

  /**
   * Whether a post was written by the current user — used to label it and
   * hide the Report action on your own post.
   * @param {Post} post
   * @returns {boolean}
   */
  isOwnPost(post: Post): boolean {
    const me = this.userService.currentUser?.id;
    return me != null && (post.user?.id ?? post.user_id) === me;
  }

  /** Debounce handle for the feed's local search input, so typing doesn't reload on every keystroke. */
  private searchDebounce?: ReturnType<typeof setTimeout>;

  constructor() {}

  /**
   * Loads the initial post feed and subscribes to user updates for change detection.
   * @returns {void}
   */
  ngOnInit(): void {
    this.loadSub = this.loadTrigger$
      .pipe(
        switchMap((mode) => {
          if (this.feedMode() === 'mine') {
            const mineLimit =
              mode === 'live' ? Math.max(this.posts().length, this.limit) : this.limit;
            const mineOffset = mode === 'live' ? 0 : this.page() * this.limit;
            return this.studentPostService
              .getMyPosts(
                mineLimit,
                mineOffset,
                this.searchQuery,
                Array.from(this.selectedCareerIds()),
                Array.from(this.selectedInterestIds()),
              )
              .pipe(map((data) => ({ mode, data }) as const));
          }

          if (mode === 'live') {
            const count = Math.max(this.posts().length, this.limit);
            return this.studentPostService
              .getPosts(
                count,
                0,
                this.searchQuery,
                Array.from(this.selectedCareerIds()),
                Array.from(this.selectedInterestIds()),
              )
              .pipe(map((data) => ({ mode, data }) as const));
          }

          const offset = this.page() * this.limit;
          return this.studentPostService
            .getPosts(
              this.limit,
              offset,
              this.searchQuery,
              Array.from(this.selectedCareerIds()),
              Array.from(this.selectedInterestIds()),
            )
            .pipe(map((data) => ({ mode, data }) as const));
        }),
      )
      .subscribe(({ mode, data }) => {
        if (mode === 'live') {
          const freshIds = new Set(data.map((post) => post.id));
          const beyondFreshWindow = this.posts().filter((post) => !freshIds.has(post.id));
          this.posts.set([...data, ...beyondFreshWindow]);
        } else {
          this.posts.update((existing) => [...existing, ...data]);
          this.hasMore.set(data.length === this.limit);
          this.page.update((p) => p + 1);
        }

        this.isLoading.set(false);
      });

    this.querySub = this.route.queryParamMap.subscribe((params) => {
      const q = params.get('q') ?? '';
      if (q !== this.searchQuery) {
        this.searchQuery = q;
        if (this.initialized) {
          this.loadPosts(true);
        }
      }
    });

    this.loadPosts(true);
    this.initialized = true;

    this.careerPageService.getTypes().subscribe((data) => {
      this.careers.set(data.map((c: { id: number; name: string }) => ({ id: c.id, name: c.name })));
    });

    this.interestService.getInterests().subscribe((data) => {
      this.interests.set(data);
    });

    this.sub = this.userService.userUpdated$.subscribe(() => {
      this.cdr.markForCheck();
    });

    this.postChangedSub = this.studentPostService.onPostChanged().subscribe((event) => {
      this.refreshPostsLive();

      if (this.openCommentsPostId === event.post_id) {
        this.loadComments(event.post_id);
      }
    });
  }

  /**
   * Unsubscribes from the user update stream on destroy.
   * @returns {void}
   */
  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.querySub?.unsubscribe();
    this.postChangedSub?.unsubscribe();
    this.loadSub?.unsubscribe();
    clearTimeout(this.searchDebounce);
  }

  /**
   * Toggles the comment section for a post. Collapses if already open;
   * otherwise opens it and fetches comments if not yet loaded.
   * @param {number} postId - The ID of the post to toggle comments for.
   * @returns {void}
   */
  toggleComments(postId: number): void {
    if (this.openCommentsPostId === postId) {
      this.openCommentsPostId = null;
    } else {
      this.openCommentsPostId = postId;
      this.loadComments(postId);
    }
  }

  /**
   * Toggles the reply input for a comment. Collapses if already active.
   * @param {number} commentId - The ID of the comment to toggle the reply input for.
   * @returns {void}
   */
  toggleReply(commentId: number): void {
    this.activeReplyId = this.activeReplyId === commentId ? null : commentId;
  }

  /**
   * Submits a new top-level comment on a post. Optimistically prepends
   * the new comment to the local list and increments the post's comment count.
   * @param {number} postId - The ID of the post to comment on.
   * @returns {void}
   */
  addComment(postId: number): void {
    const content = this.newComment?.[postId];
    if (!content || !content.trim()) return;

    this.studentPostService.createComment(postId, content).subscribe({
      next: (newComment: any) => {
        this.comments[postId] ??= [];
        newComment.user = this.currentUser;
        newComment.replies ??= [];
        this.comments[postId].unshift(newComment);
        const post = this.posts().find((post) => post.id === postId);
        if (post) {
          post.commentCount += 1;
        }
        this.newComment[postId] = '';
        this.cdr.markForCheck();
      },
      error: (err) => {
        const message = err?.error?.error || 'Failed to post comment. Please try again.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
      },
    });
  }

  /**
   * Submits a reply to an existing comment. Appends the reply to the
   * parent comment's local replies array and increments the post's comment count.
   * @param {number} postId - The ID of the post the comment belongs to.
   * @param {number} parentId - The ID of the comment being replied to.
   * @returns {void}
   */
  addReply(postId: number, parentId: number): void {
    const content = this.replyText[parentId];
    if (!content?.trim()) return;

    this.studentPostService.createComment(postId, content, parentId).subscribe({
      next: (newComment: any) => {
        const parentComment = this.comments[postId]?.find((c) => c.id === parentId);
        if (parentComment) {
          parentComment.replies ??= [];
          newComment.user = this.currentUser;
          parentComment.replies.push(newComment);
        }

        this.replyText[parentId] = '';
        const post = this.posts().find((post: any) => post.id === postId);
        if (post) {
          post.commentCount += 1;
        }
        this.activeReplyId = null;
        this.cdr.markForCheck();
      },
      error: (err) => {
        const message = err?.error?.error || 'Failed to post reply. Please try again.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
      },
    });
  }

  /**
   * Returns the current authenticated user from UserService.
   * @returns {User | null}
   */
  get currentUser() {
    return this.userService.currentUser;
  }

  /**
   * Returns the current user's uppercase initials, falling back to '?'.
   * @returns {string}
   */
  get initials(): string {
    const user = this.userService.currentUser;
    if (!user) return '?';
    const f = (user.preferredName || user.firstName)?.[0] ?? '';
    const l = user.lastName?.[0] ?? '';
    return (f + l).toUpperCase() || '?';
  }

  /**
   * Returns the current user's full name as a trimmed string.
   * @returns {string}
   */
  get fullName(): string {
    const user = this.userService.currentUser;
    if (!user) return '';
    return `${(user.preferredName || user.firstName) ?? ''} ${user.lastName ?? ''}`.trim();
  }

  /**
   * Returns initials for a post author. Accepts any user shape since post authors may not match the User model exactly.
   * @param {any} user - The post author object.
   * @returns {string}
   */
  getPostInitials(user: any): string {
    if (!user) return '?';
    const f = (user.preferredName || user.firstName)?.[0] ?? '';
    const l = user.lastName?.[0] ?? '';
    return (f + l).toUpperCase() || '?';
  }

  /**
   * Fetches all posts from the service and populates the feed.
   * @returns {void}
   */
  loadPosts(reset = false): void {
    if (reset) {
      this.page.set(0);
      this.posts.set([]);
      this.hasMore.set(true);
      this.isLoading.set(false);
    }

    if (this.isLoading() || !this.hasMore()) return;

    this.isLoading.set(true);
    this.loadTrigger$.next('paginated');
  }

  /**
   * Syncs the career filter selection from the chip listbox's (change)
   * event — which reports the full current selection, not a single
   * toggle — and reloads the feed. This is the handler the template's
   * mat-chip-listbox actually calls; it's driven one-way from the DOM to
   * avoid the [selected]-per-chip + signal feedback loop that comes from
   * also binding an external source of truth onto each individual
   * mat-chip-option.
   * @param {{ value: unknown }} event - MatChipListbox's change event.
   * @returns {void}
   */
  onCareerFilterChange(event: { value: unknown }): void {
    this.selectedCareerIds.set(new Set((event.value as number[]) ?? []));
    this.loadPosts(true);
  }

  /**
   * Same as `onCareerFilterChange`, for the interest filter listbox.
   * @param {{ value: unknown }} event - MatChipListbox's change event.
   * @returns {void}
   */
  onInterestFilterChange(event: { value: unknown }): void {
    this.selectedInterestIds.set(new Set((event.value as number[]) ?? []));
    this.loadPosts(true);
  }

  /**
   * Toggles a single career filter on/off programmatically and reloads the feed.
   * @param {number} careerId - The career ID to toggle.
   * @returns {void}
   */
  toggleCareerFilter(careerId: number): void {
    const next = new Set(this.selectedCareerIds());
    if (next.has(careerId)) {
      next.delete(careerId);
    } else {
      next.add(careerId);
    }
    this.selectedCareerIds.set(next);
    this.loadPosts(true);
  }

  /**
   * Toggles a single interest filter on/off programmatically and reloads the feed.
   * @param {number} interestId - The interest ID to toggle.
   * @returns {void}
   */
  toggleInterestFilter(interestId: number): void {
    const next = new Set(this.selectedInterestIds());
    if (next.has(interestId)) {
      next.delete(interestId);
    } else {
      next.add(interestId);
    }
    this.selectedInterestIds.set(next);
    this.loadPosts(true);
  }

  /**
   * Returns whether a career chip is currently selected — bound in the
   * template for aria-pressed/[class.selected].
   * @param {number} careerId
   * @returns {boolean}
   */
  isCareerSelected(careerId: number): boolean {
    return this.selectedCareerIds().has(careerId);
  }

  /**
   * Returns whether an interest chip is currently selected — bound in the
   * template for aria-pressed/[class.selected].
   * @param {number} interestId
   * @returns {boolean}
   */
  isInterestSelected(interestId: number): boolean {
    return this.selectedInterestIds().has(interestId);
  }

  /**
   * Clears all selected career/interest filter chips and reloads the feed.
   * @returns {void}
   */
  clearFilters(): void {
    this.selectedCareerIds.set(new Set());
    this.selectedInterestIds.set(new Set());
    this.loadPosts(true);
  }

  /**
   * Whether any filter chip is currently selected — drives the "Clear
   * filters" button's visibility.
   * @returns {boolean}
   */
  get hasActiveFilters(): boolean {
    return this.selectedCareerIds().size > 0 || this.selectedInterestIds().size > 0;
  }

  /**
   * Toggles the collapsible career/interest filter panel open/closed.
   * @returns {void}
   */
  toggleFiltersOpen(): void {
    this.filtersOpen.update((open) => !open);
  }

  /**
   * Switches between the full feed and the student's own posts, then
   * reloads from the top. Search and career/interest filters carry over
   * and apply in both modes.
   * @param {FeedMode} mode
   * @returns {void}
   */
  setFeedMode(mode: FeedMode): void {
    if (this.feedMode() === mode) return;
    this.feedMode.set(mode);
    this.openCommentsPostId = null;
    this.loadPosts(true);
  }

  /**
   * Opens the Create Post modal. The feed isn't reloaded on close — the
   * server's `post:changed` broadcast already triggers refreshPostsLive(),
   * and reloading here as well would race it (see loadTrigger$).
   * @returns {void}
   */
  openCreatePostDialog(): void {
    this.dialog.open(StudentPostDialog, {
      width: '640px',
      maxWidth: '95vw',
      autoFocus: 'first-tabbable',
      data: { careers: this.careers(), interests: this.interests() },
    });
  }

  /**
   * Handles typing in the feed's local search input. Debounced so the feed
   * doesn't reload on every keystroke — reloads from the top once typing
   * pauses. Passing an empty string (e.g. from the clear button) reloads
   * immediately.
   * @param {string} value - The current search input value.
   * @returns {void}
   */
  onSearchInput(value: string): void {
    this.searchQuery = value;
    clearTimeout(this.searchDebounce);

    if (!value.trim()) {
      this.loadPosts(true);
      return;
    }

    this.searchDebounce = setTimeout(() => this.loadPosts(true), 300);
  }

  loadMore(): void {
    this.loadPosts();
  }

  /**
   * Refetches the currently-loaded window of posts and merges the result
   * into `posts` in place, instead of loadPosts(true)'s hard reset (which
   * clears the array before refetching, causing the whole feed to flash
   * empty). Used for live-update refreshes so existing post cards are
   * updated/reordered smoothly and only genuinely new posts animate in.
   * Routed through loadTrigger$ (see its doc comment) rather than fetching
   * independently, so it can't race loadPosts().
   * @returns {void}
   */
  private refreshPostsLive(): void {
    this.loadTrigger$.next('live');
  }
  /**
   * Fetches comments for a post and stores them in the local comments map.
   * @param {number} postId - The ID of the post to load comments for.
   * @returns {void}
   */
  loadComments(postId: number): void {
    this.studentPostService.getComments(postId).subscribe((data) => {
      this.comments[postId] = data;
      this.cdr.markForCheck();
    });
  }

  /**
   * Opens the report dialog for a post. On successful report submission,
   * removes the post from the local feed.
   * @param {number} postId - The ID of the post to report.
   * @returns {void}
   */
  reportPost(postId: number): void {
    const dialogRef = this.dialog.open(ReportDialog, {
      data: { postId },
      width: '480px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.hidePost) {
        this.studentPostService.hidePost(postId).subscribe(() => {
          this.posts.update((posts) => posts.filter((p) => p.id !== postId));
          this.cdr.markForCheck();
        });
      }
    });
  }

  /**
   * Hides a post from the current user's feed after confirmation.
   * @param {number} postId - The ID of the post to hide.
   * @returns {void}
   */
  hidePost(postId: number): void {
    const dialogRef = this.dialog.open(HideConfirmDialog, {
      width: '400px',
      data: { contentType: 'post' },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.studentPostService.hidePost(postId).subscribe(() => {
          this.posts.update((posts) => posts.filter((p) => p.id !== postId));
          this.cdr.markForCheck();
        });
      }
    });
  }

  /**
   * Opens the report dialog for a comment. On successful report submission,
   * optionally hides the comment from the local view.
   * @param {number} commentId - The ID of the comment to report.
   * @param {number} postId - The ID of the post the comment belongs to.
   * @returns {void}
   */
  reportComment(commentId: number, postId: number): void {
    setTimeout(() => {
      const dialogRef = this.dialog.open(ReportDialog, {
        data: { commentId },
        width: '480px',
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result?.hideComment) {
          this.studentPostService.hideComment(commentId).subscribe(() => {
            this.comments[postId] = this.comments[postId].filter((c) => c.id !== commentId);
            this.cdr.markForCheck();
          });
        }
      });
    });
  }

  /**
   * Hides a comment from the current user's view after confirmation.
   * @param {number} commentId - The ID of the comment to hide.
   * @param {number} postId - The ID of the post the comment belongs to.
   * @returns {void}
   */
  hideComment(commentId: number, postId: number): void {
    setTimeout(() => {
      const dialogRef = this.dialog.open(HideConfirmDialog, {
        width: '400px',
        data: { contentType: 'comment' },
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          this.studentPostService.hideComment(commentId).subscribe(() => {
            this.comments[postId] = this.comments[postId].filter((c) => c.id !== commentId);
            this.cdr.markForCheck();
          });
        }
      });
    });
  }
}
